import { AUDIO_HOOKS, DECK, hops, type AudioBus as BusName, type AudioHook, type NodeId } from "@spacecrew/shared";
import { TONES } from "./tones.js";

/**
 * The ship's audio bus — a small, designed foundation rather than a beep stub.
 *
 *   master ─┬─ ambient   (beds: reactor hum)
 *           ├─ alert     (klaxons, PA)
 *           ├─ ui        (terminal feedback)
 *           ├─ sfx       (mechanical, spatialized by deck-map distance)
 *           └─ intercom  (reserved for M2: per-crew radio chains driven by commsQuality)
 *
 * Placeholder tones are synthesized; the real assets swap in behind the same hook names.
 * Audio never mutates game state — it only listens.
 */
const SPATIAL_GAIN = [1, 0.5, 0.22] as const;

interface Loop { osc: OscillatorNode; gain: GainNode; pan: StereoPannerNode; hook: AudioHook; base: number; lfo?: OscillatorNode }

class ShipAudio {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private buses!: Record<BusName | "intercom" | "music", GainNode>;
  private loops = new Map<string, Loop>();
  private _muted = false;
  /** Persisted mixer levels, 0..1 per bus (master included). */
  private levels: Record<string, number> = { master: 0.6, ambient: 0.35, alert: 0.7, ui: 0.4, sfx: 0.6, intercom: 1, music: 0.5 };
  /** Where the listener is standing — spatial cues are attenuated by graph distance from here. */
  listenerNode: NodeId = "bridge";

  get ready() { return this.ctx !== null; }
  get muted() { return this._muted; }
  /** For the intercom radio chains (M2): the AudioContext and the `intercom` bus input. */
  get context(): AudioContext | null { return this.ctx; }
  get intercomBus(): GainNode | null { return this.ctx ? this.buses.intercom : null; }

  /**
   * Build the audio graph and try to run it. Idempotent, and safe to call before any gesture:
   * Chrome starts the context suspended unless the site has earned autoplay (media engagement, or
   * the user allowing Sound for the origin), in which case the entry theme starts on load — which
   * is the point. If it stays suspended, `statechange` picks the track up the moment the first
   * click or keypress lets it run, and every gesture calls back in here to nudge `resume()`.
   */
  unlock(): void {
    if (this.ctx) { if (this.ctx.state === "suspended") void this.ctx.resume(); return; }
    try { Object.assign(this.levels, JSON.parse(localStorage.getItem("spacecrew.audio") ?? "{}")); } catch { /* defaults */ }
    const ctx = new AudioContext();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = this._muted ? 0 : this.levels.master!;
    this.master.connect(ctx.destination);
    const mk = (v: number) => { const g = ctx.createGain(); g.gain.value = v; g.connect(this.master); return g; };
    this.buses = { ambient: mk(this.levels.ambient!), alert: mk(this.levels.alert!), ui: mk(this.levels.ui!), sfx: mk(this.levels.sfx!), intercom: mk(this.levels.intercom!), music: mk(this.levels.music!) };
    ctx.onstatechange = () => { if (ctx.state === "running") this.resumeWanted(); };
    void ctx.resume();
    this.resumeWanted();
  }

  /** Start a track that was asked for while the context could not play it yet. */
  private resumeWanted(): void {
    if (!this.musicWanted || this.ctx?.state !== "running") return;
    const url = this.musicWanted;
    this.musicWanted = null;
    this.playMusic(url, { level: this.musicLevel });
  }

  setMuted(muted: boolean): void {
    this._muted = muted;
    if (this.ctx) this.master.gain.setTargetAtTime(muted ? 0 : this.levels.master!, this.ctx.currentTime, 0.02);
  }

  /** Mixer level for one bus (or "master"), 0..1. Persisted per browser. */
  level(bus: string): number { return this.levels[bus] ?? 1; }
  setLevel(bus: string, value: number): void {
    const v = Math.max(0, Math.min(1, value));
    this.levels[bus] = v;
    try { localStorage.setItem("spacecrew.audio", JSON.stringify(this.levels)); } catch { /* private mode */ }
    if (!this.ctx) return;
    const node = bus === "master" ? this.master : this.buses[bus as BusName | "intercom" | "music"];
    if (node && !(bus === "master" && this._muted)) node.gain.setTargetAtTime(v, this.ctx.currentTime, 0.03);
  }

  private spatial(hook: AudioHook, nodeId?: NodeId): { gain: number; pan: number } {
    if (!AUDIO_HOOKS[hook].spatial || !nodeId) return { gain: 1, pan: 0 };
    const d = hops(this.listenerNode, nodeId);
    const gain = SPATIAL_GAIN[Math.min(d, SPATIAL_GAIN.length - 1)] ?? 0.1;
    const pan = (DECK.nodes[nodeId].xy[0] - DECK.nodes[this.listenerNode].xy[0]) * 1.6;
    return { gain, pan: Math.max(-1, Math.min(1, pan)) };
  }

  /** One-shot placeholder: short envelope, optional pitch glide to hz2. */
  play(hook: AudioHook, opts: { nodeId?: NodeId } = {}): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const [hz, ms, wave, hz2] = TONES[hook];
    if (ms === 0) return; // loop-only hook
    const { gain, pan } = this.spatial(hook, opts.nodeId);
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    const panner = ctx.createStereoPanner();
    osc.type = wave;
    osc.frequency.setValueAtTime(hz, t0);
    if (hz2) osc.frequency.exponentialRampToValueAtTime(hz2, t0 + ms / 1000);
    env.gain.setValueAtTime(0, t0);
    env.gain.linearRampToValueAtTime(gain, t0 + 0.006);
    env.gain.setTargetAtTime(0, t0 + ms / 1000 - 0.03, 0.02);
    panner.pan.value = pan;
    osc.connect(env).connect(panner).connect(this.buses[AUDIO_HOOKS[hook].bus]);
    osc.start(t0);
    osc.stop(t0 + ms / 1000 + 0.1);
  }

  /** Idempotent continuous sound keyed by `key`. Re-call with the same key to retune/re-spatialize. */
  loop(key: string, hook: AudioHook, opts: { nodeId?: NodeId; detune?: number; rate?: number; level?: number } = {}): void {
    if (!this.ctx) { setTimeout(() => this.loop(key, hook, opts), 600); return; } // not unlocked yet
    const ctx = this.ctx;
    if (!ctx) return;
    const { gain, pan } = this.spatial(hook, opts.nodeId);
    const level = gain * (opts.level ?? 1);
    let l = this.loops.get(key);
    if (!l) {
      const [hz, , wave] = TONES[hook];
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      const p = ctx.createStereoPanner();
      osc.type = wave;
      osc.frequency.value = hz;
      g.gain.value = 0;
      osc.connect(g).connect(p).connect(this.buses[AUDIO_HOOKS[hook].bus]);
      osc.start();
      l = { osc, gain: g, pan: p, hook, base: hz };
      if (hook === "move.footsteps.loop") {
        // Gate the tone into a ~2 Hz step rhythm so it reads as footfalls rather than a drone.
        const lfo = ctx.createOscillator();
        const depth = ctx.createGain();
        lfo.type = "square"; lfo.frequency.value = 2.2; depth.gain.value = 0.25;
        lfo.connect(depth).connect(g.gain);
        lfo.start();
        l.lfo = lfo;
      }
      this.loops.set(key, l);
    }
    const t = ctx.currentTime;
    l.gain.gain.setTargetAtTime(level * 0.5, t, 0.05);
    l.pan.pan.setTargetAtTime(pan, t, 0.05);
    if (opts.detune !== undefined) l.osc.detune.setTargetAtTime(opts.detune, t, 0.1);
    if (opts.rate !== undefined) l.osc.frequency.setTargetAtTime(l.base * opts.rate, t, 0.08);
  }

  /** Sample-backed loop (ambience beds). Fetch+decode once; loops seamlessly; level adjustable per call. */
  private samples = new Map<string, { src: AudioBufferSourceNode; gain: GainNode } | "loading">();
  loopSample(key: string, url: string, opts: { level?: number; bus?: BusName } = {}): void {
    const ctx = this.ctx;
    // Asked for before the context can play — hold it and try again shortly, rather than dropping
    // the room tone silently. `loop()` has always done this; this one did not.
    if (!ctx || ctx.state !== "running") {
      if (ctx) void ctx.resume();
      window.setTimeout(() => this.loopSample(key, url, opts), 600);
      return;
    }
    const existing = this.samples.get(key);
    if (existing === "loading") return;
    if (existing) { existing.gain.gain.setTargetAtTime((opts.level ?? 1), ctx.currentTime, 0.3); return; }
    this.samples.set(key, "loading");
    void fetch(url).then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.arrayBuffer(); }).then((b) => ctx.decodeAudioData(b)).then((buf) => {
      if (this.samples.get(key) !== "loading") return;
      const src = ctx.createBufferSource(); src.buffer = buf; src.loop = true;
      const gain = ctx.createGain(); gain.gain.value = 0;
      src.connect(gain).connect(this.buses[opts.bus ?? "ambient"]);
      src.start();
      gain.gain.setTargetAtTime(opts.level ?? 1, ctx.currentTime, 1.2);
      this.samples.set(key, { src, gain });
    }).catch(() => { this.samples.delete(key); /* placeholder tones carry on */ });
  }
  /**
   * The menu bed. Not ambience and not a hook: one scored track on its own bus, playing only on
   * the crew access terminal. It is written as a piece with an arc (it thins out, then lifts), so
   * it is looped with a long crossfade rather than butt-joined — two overlapping passes, the tail
   * of one fading under the head of the next, scheduled on the audio clock so it never drifts.
   */
  private musicBuf: AudioBuffer | null = null;
  private musicWanted: string | null = null;
  private musicLevel = 1;
  private music: { url: string; gain: GainNode; srcs: AudioBufferSourceNode[]; timer: number } | null = null;

  playMusic(url: string, opts: { level?: number; crossfade?: number } = {}): void {
    this.musicLevel = opts.level ?? 1;
    const ctx = this.ctx;
    // No context, or one the browser will not let run yet: hold the track and start it from
    // `statechange`. Scheduling sources against a suspended clock would strand them in the past.
    if (!ctx || ctx.state !== "running") { this.musicWanted = url; if (ctx) void ctx.resume(); return; }
    if (this.music?.url === url) { this.music.gain.gain.setTargetAtTime(this.musicLevel, ctx.currentTime, 0.4); return; }
    if (this.music) this.stopMusic();
    const gain = ctx.createGain();
    gain.gain.value = this.musicLevel;
    gain.connect(this.buses.music);
    const play = { url, gain, srcs: [] as AudioBufferSourceNode[], timer: 0 };
    this.music = play;
    const cf = opts.crossfade ?? 5;
    const begin = (buf: AudioBuffer) => {
      if (this.music !== play) return;
      const dur = buf.duration;
      const pass = (at: number) => {
        if (this.music !== play) return;
        const src = ctx.createBufferSource(); src.buffer = buf;
        const g = ctx.createGain();
        const fadeIn = Math.min(cf, 2.5);
        g.gain.setValueAtTime(0.0001, at);
        g.gain.linearRampToValueAtTime(1, at + fadeIn);
        g.gain.setValueAtTime(1, at + dur - cf);
        g.gain.linearRampToValueAtTime(0.0001, at + dur);
        src.connect(g).connect(gain);
        src.start(at); src.stop(at + dur + 0.2);
        src.onended = () => { play.srcs = play.srcs.filter((s) => s !== src); };
        play.srcs.push(src);
        // Queue the next pass a second before it is due to start, so the schedule stays ahead.
        const next = at + dur - cf;
        play.timer = window.setTimeout(() => pass(next), Math.max(200, (next - ctx.currentTime - 1) * 1000));
      };
      pass(ctx.currentTime + 0.05);
    };
    if (this.musicBuf) { begin(this.musicBuf); return; }
    void fetch(url).then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.arrayBuffer(); })
      .then((b) => ctx.decodeAudioData(b))
      .then((buf) => { this.musicBuf = buf; begin(buf); })
      .catch(() => { if (this.music === play) this.music = null; /* silence is survivable */ });
  }

  /** Boarding kills the music: past the hatch the only score is the ship. */
  stopMusic(fade = 1.4): void {
    const play = this.music;
    this.musicWanted = null;
    if (!play || !this.ctx) { this.music = null; return; }
    this.music = null;
    clearTimeout(play.timer);
    play.gain.gain.cancelScheduledValues(this.ctx.currentTime);
    play.gain.gain.setTargetAtTime(0, this.ctx.currentTime, fade / 3);
    const srcs = play.srcs;
    setTimeout(() => { for (const s of srcs) { try { s.stop(); } catch { /* already done */ } } }, fade * 1000 + 400);
  }

  stopSample(key: string): void {
    const s = this.samples.get(key);
    if (!s || s === "loading" || !this.ctx) { this.samples.delete(key); return; }
    s.gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.5);
    const src = s.src; setTimeout(() => { try { src.stop(); } catch { /* stopped */ } }, 2500);
    this.samples.delete(key);
  }

  stop(key: string): void {
    const l = this.loops.get(key);
    const ctx = this.ctx;
    if (!l || !ctx) return;
    this.loops.delete(key);
    const t = ctx.currentTime;
    l.gain.gain.setTargetAtTime(0, t, 0.04);
    l.osc.stop(t + 0.3);
    l.lfo?.stop(t + 0.3);
  }

  /** Stop every synthesised loop, leaving sampled beds (the room tone) running. */
  stopLoops(): void { for (const k of [...this.loops.keys()]) this.stop(k); }

  stopAll(): void { this.stopLoops(); for (const k of [...this.samples.keys()]) this.stopSample(k); }

  /** What the mixer and the audio clock are actually doing. Exposed as `__audio()` for debugging. */
  report(): Record<string, unknown> {
    return {
      context: this.ctx?.state ?? "never created",
      muted: this._muted,
      levels: { ...this.levels },
      music: this.music ? "playing" : this.musicWanted ? "waiting for the context" : "none",
      samples: [...this.samples.entries()].map(([k, v]) => `${k}: ${v === "loading" ? "loading" : "playing"}`),
      loops: [...this.loops.keys()],
    };
  }

}

export const audio = new ShipAudio();

// Always available: when a machine is silent, the first question is what the audio clock and the
// mixer are doing, and that answer should not require a debug build to get at.
declare global { interface Window { __audio?: () => Record<string, unknown> } }
if (typeof window !== "undefined") window.__audio = () => audio.report();
